import React, { createRef, useState, useEffect } from 'react';
import { CustomInput, DropDown } from '../../../components/common';
import RichTextEditor from '../../../components/rich-text-editor/rich-text-editor';
import { useExternalProviderPlans } from '../../../hooks/useExternalProviderPlans';
import { Package, useUpdatePackage } from '../../../hooks/useClients';
import isURL from '../../../components/common/url-validation';
interface PackageDetailsProps {
  packageData: Package;
  setSaveDataModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const PackageDetails: React.FunctionComponent<PackageDetailsProps> = ({
  packageData,
  setSaveDataModal,
}): JSX.Element => {
  const { data: externalProviderPlans } = useExternalProviderPlans();
  const { updatePackage } = useUpdatePackage(packageData.client_id!, packageData.id!);

  const [packageName, setpackageName] = useState<string>('');
  const [packageCode, setpackageCode] = useState<string>('');
  const [benefitsDescription, setBenefitsDescription] = useState<string | undefined>(undefined);
  const [saveWebAppUrl, setsaveWebAppUrl] = useState<string | undefined>(undefined);
  const [packageId, setpackageId] = useState<number | undefined>(undefined);
  const [serviceProvider, setServiceProvider] = useState<number | undefined>(undefined);
  const [costPerMinuteCents, setCostPerMinuteCents] = useState<number>(0);
  const [clientPerMemberPerMonth, setClientPerMemberPerMonth] = useState<number>(0);
  const [clientCostPerMinute, setClientCostPerMinute] = useState<number>(0);
  const [clientCostPerCall, setClientCostPerCall] = useState<number>(0);
  const [monthlyPlatformAccessFee, setMonthlyPlatformAccessFee] = useState<number>(0);
  const [annualPlatformAccessFee, setAnnualPlatformAccessFee] = useState<number>(0);
  const [myPlanImage, setMyPlanImage] = useState<string | null>(null);
  const [preLoadedMyPlanImage, setPreLoadedMyPlanImage] = useState<boolean>(true);
  const [enableSaveOption, setenableSaveOption] = useState(false);
  const [error, seterror] = useState(false);
  const [webappTheme, setWebappTheme] = useState<string | undefined>(undefined);

  const fileInput: any = createRef();

  const onFileChange = async (event: any) => {
    try {
      if (event.target.files && event.target.files[0]) {
        let reader = new FileReader();

        reader.onload = (e: any) => {
          //Set the Base64 string return from FileReader as source.
          const image = new Image();
          image.src = e.target.result;

          //Validate the File Height and Width.
          image.onload = () => {
            setMyPlanImage(image.src);
            setPreLoadedMyPlanImage(false);
          };
        };
        reader.readAsDataURL(event.target.files[0]);
        setenableSaveOption(true);
      }
    } catch (error) {
      console.error('err--', error);
    }
  };

  const hadleImageinput = () => {
    fileInput.current.click();
  };

  const Validation = () => {
    if (
      !packageName ||
      !packageCode ||
      (saveWebAppUrl !== undefined && saveWebAppUrl?.trim() !== '' && saveWebAppUrl && !isURL(saveWebAppUrl?.trim()))
    ) {
      seterror(true);
      return false;
    }
    seterror(false);
    return true;
  };

  const handleSave = () => {
    const payload: Partial<Package> = {
      benefits_description: benefitsDescription,
      code: packageCode?.replaceAll(' ', ''),
      cost_per_minute_cents: costPerMinuteCents,
      client_fee_per_member_per_month: clientPerMemberPerMonth,
      client_cost_per_minute: clientCostPerMinute,
      client_cost_per_call_unit: clientCostPerCall,
      monthly_platform_access_fee: monthlyPlatformAccessFee,
      annual_platform_access_fee: annualPlatformAccessFee,
      name: packageName?.trim(),
      // None keeps state undefined; coerce to null for the integer FK
      external_plan_id: serviceProvider ?? null,
      web_app_url:
        saveWebAppUrl === null || saveWebAppUrl?.trim() === '' || !saveWebAppUrl ? undefined : saveWebAppUrl?.trim(),
      theme_name: webappTheme,
      ...(preLoadedMyPlanImage ? {} : { my_plan_file_url: myPlanImage }),
    };
    if (Validation()) {
      updatePackage.mutate(payload, {
        onSuccess: () => {
          setenableSaveOption(false);
        },
      });
    }
  };

  useEffect(() => {
    if (packageData) {
      setpackageName(packageData.name);
      setpackageCode(packageData.code);
      setsaveWebAppUrl(packageData.web_app_url);
      setpackageId(packageData.id);
      setenableSaveOption(false);
      setWebappTheme(packageData?.theme_name);
      setServiceProvider(packageData.external_plan_id ?? undefined);
      setCostPerMinuteCents(packageData.cost_per_minute_cents);
      setClientPerMemberPerMonth(packageData.client_fee_per_member_per_month);
      setClientCostPerMinute(packageData.client_cost_per_minute);
      setClientCostPerCall(packageData.client_cost_per_call_unit);
      setMonthlyPlatformAccessFee(packageData.monthly_platform_access_fee);
      setAnnualPlatformAccessFee(packageData.annual_platform_access_fee);
      setBenefitsDescription(packageData?.benefits_description);
      if (packageData?.my_plan_file_url) {
        setMyPlanImage(packageData?.my_plan_file_url);
        setPreLoadedMyPlanImage(false);
      } else {
        setMyPlanImage(null);
        setPreLoadedMyPlanImage(true);
      }
    }
  }, [packageData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSaveDataModal(enableSaveOption);
  }, [enableSaveOption, setSaveDataModal]);

  return (
    <>
      <div className="text-gray-dark gray-border-line mb-2 flex h-10 w-full items-center justify-between border-b bg-transparent px-2 text-sm font-bold">
        <div>PLAN CONFIGURATION</div>
        <div>
          <button
            className={
              'h-6 w-full rounded-full px-2 text-center text-sm text-white ' +
              (enableSaveOption ? ' stacked-bar-blue-400' : ' bg-gray-background-light cursor-not-allowed')
            }
            onClick={handleSave}
            disabled={!enableSaveOption}
            data-testid="save-package"
          >
            SAVE
          </button>
        </div>
      </div>
      <div className="flex justify-evenly gap-7">
        <div className="w-full py-4">
          <CustomInput
            inputType={'text'}
            value={packageName}
            setValue={(data) => {
              setenableSaveOption(true);
              setpackageName(data);
            }}
            Label={'Plan Name'}
            Error={error && !packageName}
            ErrorMessage={'Field is Required'}
            dataTestId="plan-name"
          />
        </div>
        <div className="w-full py-4">
          <CustomInput
            inputType={'text'}
            value={packageCode}
            setValue={(data) => {
              setenableSaveOption(true);
              setpackageCode(data);
            }}
            Label={'Plan Code'}
            Error={error && !packageCode}
            ErrorMessage={'Field is Required'}
            dataTestId="plan-code"
          />
        </div>
      </div>
      <div className="flex justify-evenly gap-7">
        <div className="w-full py-4">
          <label className="text-gray-dark mb-2 block leading-4">Benefits Description</label>
          <p className="mb-2 text-sm text-gray-500">
            This is the benefits section that will be displayed in the MWA Plan Details section.
          </p>
          <RichTextEditor
            key={`benefits-description-${packageId}`}
            content={benefitsDescription || ''}
            onChange={(value) => {
              setBenefitsDescription(value);
              setenableSaveOption(true);
            }}
            data-testid="benefits-description"
          />
          <div className="my-2">
            <p className="text-gray-dark pt-1 font-bold">My Plan Image</p>
            <p className="text-gray-dark py-2 pt-1 text-sm font-normal">
              Displays on the Member Web App Home page and My Plan page. Should be 9:16 and a minimum of 330px wide to
              look good
            </p>
            <div className="flex w-full">
              <div
                className={`${
                  !myPlanImage ? 'cursor-pointer' : ''
                } bg-gray-background-light aspect-ratio-container w-full rounded-sm`}
                onClick={() => hadleImageinput()}
                data-testid="my-plan-image"
              >
                {myPlanImage ? (
                  <div className="aspect-ratio-div">
                    <img className="aspect-ratio-container-img" src={myPlanImage} alt="clientsImage" />
                  </div>
                ) : (
                  <div className="flex h-56 items-center justify-center">
                    <button className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-4xl">
                      +
                    </button>
                    <input
                      ref={fileInput}
                      id="file-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onFileChange(e)}
                      onClick={() => {
                        fileInput.current.value = null;
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="pl-4">
                <p className="text-gray-dark pt-4 font-normal">1280 x 720 px</p>
                <div className="aspect-ratio-container w-full pt-2">
                  {myPlanImage && (
                    <button
                      className="my-2 w-full rounded-full bg-gray-300 p-2 text-center text-sm text-white"
                      onClick={() => {
                        setMyPlanImage(null);
                        setPreLoadedMyPlanImage(false);
                        setenableSaveOption(true);
                      }}
                      data-testid="remove-my-plan-image"
                    >
                      REMOVE
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full py-4">
          <div className={'mt-5'}>
            <DropDown
              dataTestId="web-app-theme"
              Label={'Web App Theme'}
              value={webappTheme !== null ? webappTheme : ''}
              setValue={(data) => {
                setWebappTheme(data === '' ? null : data);
                setenableSaveOption(true);
              }}
            >
              <option value="">Default Theme</option>
              <option value="Clever">CleverRx</option>
            </DropDown>
          </div>
          <div className={'mt-5'}>
            <DropDown
              dataTestId="professional-services-provider"
              Label={'Professional Services Provider'}
              value={serviceProvider ?? ''}
              setValue={(data) => {
                setenableSaveOption(true);
                if (data === '') {
                  setServiceProvider(undefined);
                  return;
                }
                const id = Number(data);
                setServiceProvider(Number.isFinite(id) ? id : undefined);
              }}
            >
              <option value={''}>None</option>
              {externalProviderPlans
                ?.filter((item: any) => item.id != null)
                .map((item: any) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </DropDown>
          </div>
          <div className={'mt-5'}>
            <CustomInput
              Label={'Member Cost Per Minute'}
              inputType={'text'}
              value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                costPerMinuteCents / 100,
              )}
              setValue={(data) => {
                const valueInDollars = data.endsWith('.') ? data : parseFloat(data.replace(/[^0-9]/g, '')) / 100;
                setenableSaveOption(true);
                setCostPerMinuteCents(valueInDollars * 100);
              }}
              Error={error && !costPerMinuteCents}
              ErrorMessage={'Field is Required'}
              dataTestId="member-cost-per-minute"
            />
          </div>
          <div className={'mt-5'}>
            <CustomInput
              Label={'Client Cost Per Minute'}
              inputType={'text'}
              value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                clientCostPerMinute / 100,
              )}
              setValue={(data) => {
                const valueInDollars = data.endsWith('.') ? data : parseFloat(data.replace(/[^0-9]/g, '')) / 100;
                setenableSaveOption(true);
                setClientCostPerMinute(valueInDollars * 100);
              }}
              Error={error && !clientCostPerMinute}
              ErrorMessage={'Field is Required'}
              dataTestId="client-cost-per-minute"
            />
          </div>
          <div className={'mt-5'}>
            <CustomInput
              Label={'Client Cost Per Call Unit'}
              inputType={'text'}
              value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                clientCostPerCall / 100,
              )}
              setValue={(data) => {
                const valueInDollars = data.endsWith('.') ? data : parseFloat(data.replace(/[^0-9]/g, '')) / 100;
                setenableSaveOption(true);
                setClientCostPerCall(valueInDollars * 100);
              }}
              Error={error && !clientCostPerCall}
              ErrorMessage={'Field is Required'}
              dataTestId="client-cost-per-call-unit"
            />
          </div>
          <div className={'mt-5'}>
            <CustomInput
              Label={'Client Fee Per Member Per Month'}
              inputType={'text'}
              value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                clientPerMemberPerMonth / 100,
              )}
              setValue={(data) => {
                const valueInDollars = data.endsWith('.') ? data : parseFloat(data.replace(/[^0-9]/g, '')) / 100;
                setenableSaveOption(true);
                setClientPerMemberPerMonth(valueInDollars * 100);
              }}
              Error={error && !clientPerMemberPerMonth}
              ErrorMessage={'Field is Required'}
              dataTestId="client-fee-per-member-per-month"
            />
          </div>
          <div className={'mt-5'}>
            <CustomInput
              Label={'Monthly Platform Access Fee'}
              inputType={'text'}
              value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                monthlyPlatformAccessFee / 100,
              )}
              setValue={(data) => {
                const valueInDollars = data.endsWith('.') ? data : parseFloat(data.replace(/[^0-9]/g, '')) / 100;
                setenableSaveOption(true);
                setMonthlyPlatformAccessFee(valueInDollars * 100);
              }}
              Error={error && !monthlyPlatformAccessFee}
              ErrorMessage={'Field is Required'}
              dataTestId="monthly-platform-access-fee"
            />
          </div>
          <div className={'mt-5'}>
            <CustomInput
              Label={'Annual Platform Access Fee'}
              inputType={'text'}
              value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                annualPlatformAccessFee / 100,
              )}
              setValue={(data) => {
                const valueInDollars = data.endsWith('.') ? data : parseFloat(data.replace(/[^0-9]/g, '')) / 100;
                setenableSaveOption(true);
                setAnnualPlatformAccessFee(valueInDollars * 100);
              }}
              Error={error && !annualPlatformAccessFee}
              ErrorMessage={'Field is Required'}
              dataTestId="annual-platform-access-fee"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PackageDetails;
